/**
 * A3 -- the Resilience Lead's route to declaring a tolerance.
 *
 * The API has admitted her since B4 (`require_ibs_owner_or_operator`
 * takes the IBS's owner, the head of operational resilience, or an
 * admin), but the only form was inside the owner panel, which renders
 * only for someone the ESTATE records as that IBS's owner. So every
 * route she was given -- the TOLERANCE tile, Getting Started, the war
 * room's "declare for" link -- ended on a page where she could read the
 * gap and do nothing about it.
 *
 * Rendered only when the owner panel is NOT: an owner who is also the
 * Resilience Lead should see one form, not two, and two declare buttons
 * on one page is an invitation to create the duplicate drafts C1 spent
 * a phase removing.
 *
 * The authority test is best-effort, as everywhere on the client: the
 * API is the real gate. It errs towards showing the form to someone the
 * API may refuse rather than hiding it from someone it would accept,
 * because a refusal explains itself (A1) and an absent control does not.
 */
import { useTranslation } from "react-i18next";

import { Card } from "shared/components/Card";
import { useMe } from "shared/api/useMe";
import { ToleranceDeclarationForm } from "twin/tolerance/ToleranceDeclarationForm";
import { useMyEstate } from "twin/home/useMyEstate";
import { ToleranceSigners } from "shared/tolerance/ToleranceSigners";
import { useToleranceState } from "shared/tolerance/useToleranceState";
import { asSentence, toleranceSentence } from "shared/tolerance/toleranceSentence";

export function OperatorTolerancePanel({ ibsId }: { ibsId: string | undefined }) {
  const { t } = useTranslation();
  const me = useMe();
  const estate = useMyEstate();

  const owned = (estate.data?.ibses ?? []).some((i) => i.id === ibsId);
  const isOperator =
    (me.data?.realm_roles ?? []).includes("head_of_opsres") ||
    (me.data?.is_admin ?? false);

  const state = useToleranceState();
  const row = (state.data?.rows ?? []).find((r) => r.ibs_service_id === ibsId);

  // The owner panel already carries the form for an owner.
  if (!ibsId || owned) return null;
  // Round 20 V5 -- a reader who can declare nothing here was shown no
  // panel at all, so a service with no owner read as if it had no
  // tolerance either. Say what is in force and why there is no form.
  if (!isOperator) {
    if (!row) return null;
    return (
      <Card
        testid="tolerance-readonly-panel"
        title={t("toleranceMetrics.declareHeading")}
        icon="ibs"
        className="space-y-1"
      >
        <p className="text-xs text-slate-700">
          {asSentence(toleranceSentence(t, row))}
        </p>
        <ToleranceSigners row={row} />
        <p data-testid="tolerance-no-form-why" className="text-xs text-slate-500">
          {row.owner_recorded === false
            ? t("toleranceMetrics.noFormNoOwner")
            : t("toleranceMetrics.noFormNotYours")}
        </p>
      </Card>
    );
  }

  return (
    <Card
      testid="operator-tolerance-panel"
      title={t("toleranceMetrics.declareHeading")}
      icon="ibs"
      className="space-y-3"
    >
      {row && <ToleranceSigners row={row} />}
      <ToleranceDeclarationForm ibsId={ibsId} testIdPrefix="operator-declare" />
    </Card>
  );
}
